import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import cryptoRandomString from "crypto-random-string";
import { toast } from "react-toastify";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import FileUpload from "../common/FileUpload";
import ShareModal from "../common/ShareModal";
import {
  Input,
  Select,
  Checkbox,
  FormCheckbox,
  RadioButton,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
} from "../../scripts/formScripts";
import {
  getFieldDate,
  getFieldName,
  getFieldValue,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";
import {
  getAttachments,
  getFosterBeds,
  sendEmailMessage,
  html2PdfWithStyle,
} from "../services/soar";

function SAFYILIncidentReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
      return submissionResponse;
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [newAmendment, setNewAmendment] = useState("");
  const [fileAttachments, setFileAttachments] = useState([]);

  const loadFiles = useCallback(async () => {
    if (submissionid) {
      const response = await getAttachments(submissionid);
      if (response?.data.length > 0) {
        setFileAttachments(response.data);
      }
    }
  }, [submissionid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    if (!data.FosterHome || getFieldValue(data.FosterHome)) {
      return;
    }

    let ignore = false;
    const loadFosterBeds = async () => {
      const beds = await getFosterBeds(clientid);

      if (!ignore) {
        const activeBeds = beds.filter(
          (bed) => bed.ccx_actualdischarge === undefined
        );

        if (activeBeds.length > 0) {
          const newestActiveBed = activeBeds.sort((a, b) => {
            const aDate = moment(getFieldDate(a.ccx_actualadmission));
            const bDate = moment(getFieldDate(b.ccx_actualadmission));

            if (aDate.isBefore(bDate)) return 1;
            if (bDate.isBefore(aDate)) return -1;
            return 0;
          })[0];

          const obj = { ...data };
          obj.FosterHome["@value"] = getFieldName(
            newestActiveBed.ccx_fostercarehome
          );
          setData(obj);
        }
      }
    };

    loadFosterBeds();

    return () => {
      ignore = true;
    };
  }, [clientid, data]);

  const resolutionAmendmentsMemo = useMemo(() => {
    if (
      !getFieldValue(data.ResolutionAmendments) ||
      !Array.isArray(JSON.parse(getFieldValue(data.ResolutionAmendments)))
    ) {
      return [];
    }

    return JSON.parse(getFieldValue(data.ResolutionAmendments));
  }, [data]);

  const getMainEmailBody = (selectedProgram) => {
    const siteUrl =
      process.env.REACT_APP_ENVIRONMENT === "Production"
        ? "safyportal.safy.org"
        : "safyportaltest.safy.org";

    return `
<p>
  Link: https://${siteUrl}/share/${
      JSON.parse(formsyConfig.SetupConfiguration[0]).formPath
    }/${formsyDetails.FormVersionId}/${formsyDetails.ContactId}/${
      formsyDetails.SubmissionId
    }
</p>
<p>
  <b>Incident Type:</b> ${getFieldValue(data.TypeOfIncident)}
</p>
<p>
  <b>Client Name:</b> ${getFieldName(selectedProgram.ccx_patient)}
  <br/>
  <b>Date of birth:</b> ${getFieldDate(clientData.client.dob)}
</p>
<p>
  <b>Program:</b> ${getFieldName(selectedProgram.ccx_programunit)}
</p>
<p>
  <b>Incident Resolved:</b> ${getFieldValue(data.ResolvedStatus)}
</p>
`;
  };

  const getIncidentDescription = () => {
    switch (data.TypeOfIncident["@value"]) {
      case "Abuse/Neglect Allegation":
        return `<p><b>${data.AbuseDescription["@label"]}</b>: ${data.AbuseDescription["@value"]}</p>`;
      case "Client/Immediate Family Member Death":
        return `<p><b>${data.DeathDescription["@label"]}</b>: ${data.DeathDescription["@value"]}</p>`;
      case "Homicidal Thoughts, Gestures, Threats":
        return `<p><b>${data.HomicidalThoughtsDescription["@label"]}</b> ${data.HomicidalThoughtsDescription["@value"]}</p>`;
      case "Psychiatric Emergency, excluding SI, HI or Self-Harm":
        return `<p><b>${data.PsychiatricEmergencyDescription["@label"]}</b> ${data.PsychiatricEmergencyDescription["@value"]}</p>`;
      case "Self-Harm Behaviors":
        return `<p><b>${data.SelfHarmBehaviorsDescription["@label"]}</b> ${data.SelfHarmBehaviorsDescription["@value"]}</p>`;
      case "Suicidal Thoughts, Gestures, Threats":
        return `<p><b>${data.SuicidalThoughtsDescription["@label"]}</b> ${data.SuicidalThoughtsDescription["@value"]}</p>`;
      case "Suicide Attempt":
        return `<p><b>${data.SuicideAttemptDescription["@label"]}</b> ${data.SuicideAttemptDescription["@value"]}</p>`;
      case "Illegal Behavior (to include substance use)":
        return `<p><b>${data.IllegalBehaviorDescription["@label"]}</b> ${data.IllegalBehaviorDescription["@value"]}</p>`;
      case "Aggression/Destruction towards people/property":
        return `<p><b>${data.AggressionDescription["@label"]}</b> ${data.AggressionDescription["@value"]}</p>`;
      case "Car Accident with SAFY Employee":
        return `<p><b>${data.CarAccidentDescription["@label"]}</b> ${data.CarAccidentDescription["@value"]}</p>`;
      case "Other":
        return `<p><b>${data.OtherTypeDescription["@label"]}</b> ${data.OtherTypeDescription["@value"]}</p>`;
      default:
        return "";
    }
  };

  const getExtraEmailBody = () => {
    switch (data.TypeOfIncident["@value"]) {
      case "Abuse/Neglect Allegation":
        return `<p><b>Outcome</b>: ${data.AbuseOutcome["@value"]}</p>`;
      case "Homicidal Thoughts, Gestures, Threats":
        return `<p><b>Outcome</b>: ${data.HomicidalThoughtsOutcome["@value"]}</p>`;
      case "Psychiatric Emergency, excluding SI, HI or Self-Harm":
        return `<p><b>Outcome</b>: ${data.PsychiatricEmergencyOutcome["@value"]}</p>`;
      case "Suicidal Thoughts, Gestures, Threats":
        return `<p><b>Outcome</b>: ${data.SuicidalThoughtsOutcome["@value"]}</p>`;
      case "Suicide Attempt":
        return `<p><b>Outcome</b>: ${data.SuicideAttemptOutcome["@value"]}</p>`;
      case "Illegal Behavior (to include substance use)":
        return `<p><b>Outcome</b>: ${data.IllegalBehaviorOutcome["@value"]}</p>`;
      case "Aggression/Destruction towards people/property":
        return `<p><b>Outcome</b>: ${data.AggressionOutcome["@value"]}</p>`;
      case "Other":
        return `<p><b>Outcome</b>: ${data.OtherTypeOutcome["@value"]}</p>`;
      default:
        return "";
    }
  };
  const addAmendment = async () => {
    let amendments = [];

    if (getFieldValue(data.ResolutionAmendments)) {
      const resolutionAmendments = JSON.parse(
        getFieldValue(data.ResolutionAmendments)
      );
      amendments = Array.isArray(resolutionAmendments)
        ? resolutionAmendments
        : [];
    }

    const amendment = {
      _id: cryptoRandomString(10),
      CreatedBy: safyInfo.user.displayName,
      CreatedOn: new Date(),
      Amendment: newAmendment,
    };
    amendments.push(amendment);

    const obj = { ...data };
    obj.ResolutionAmendments["@value"] = JSON.stringify(amendments);
    setData(obj);

    handleSubmit(obj);
    setNewAmendment("");

    const selectedProgram = clientData.programs.filter(
      (option) =>
        option.ccx_client_programid === getFieldValue(data.SelectedProgram)
    )[0];

    if (!selectedProgram?.["unit.sor_irconfig"]) {
      return;
    }

    let config;

    try {
      config = JSON.parse(selectedProgram["unit.sor_irconfig"]);
    } catch (e) {
      if (e.name === "SyntaxError") {
        toast.error("Error parsing Program Unit's IR Config");
      } else {
        console.log(e.message);
      }
    }

    if (!config?.Amended) {
      return;
    }
    const printStyle = `body {
      font-family: "Times New Roman";
      font-size: .75rem;
    }`;

    const pdf = await html2PdfWithStyle(pdfRef.current, printStyle);
    let attachments = [
      {
        filebytes: pdf,
        filename: `${formsyConfig.FormDescription}.pdf`,
        filetype: "application/pdf",
      },
    ];

    if (fileAttachments.length > 0) {
      attachments = attachments.concat(
        fileAttachments
          .filter((attachment) => attachment.FileType === "application/pdf")
          .map((attachment) => {
            return {
              filebytes: attachment.FileContent.split(",")[1],
              filename: attachment.FileName,
              filetype: attachment.FileType,
            };
          })
      );
    }

    const incidentType = getFieldValue(obj.TypeOfIncident);
    let recipientList = [...config.Amended];

    if (incidentType === "Suicide Attempt") {
      recipientList = recipientList.concat(config.SuicideAttempt);
    }

    sendEmailMessage(
      recipientList,
      undefined,
      `Incident Report Amended - ${incidentType}`,
      `${getMainEmailBody(selectedProgram)}
${getIncidentDescription()}
${getExtraEmailBody()}
<p>Amended by: ${safyInfo.user.displayName}, ${safyInfo.user.domainName}</p>`,
      undefined,
      attachments
    );
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);

      const selectedProgram = clientData.programs.filter(
        (option) =>
          option.ccx_client_programid === getFieldValue(data.SelectedProgram)
      )[0];

      const response = await handleSubmit(
        null,
        formsyDetailsObj,
        formsyDetailsObj.FinalSigned === "1" ? undefined : null,
        sigArray
      );

      if (response?.Id && selectedProgram?.["unit.sor_irconfig"]) {
        let config;

        try {
          config = JSON.parse(selectedProgram["unit.sor_irconfig"]);
        } catch (e) {
          if (e.name === "SyntaxError") {
            toast.error("Error parsing Program Unit's IR Config");
          } else {
            console.log(e.message);
          }
        }

        if (config?.Signed) {
          const printStyle = `body {
            font-family: "Times New Roman";
            font-size: .75rem;
          }`;

          const pdf = await html2PdfWithStyle(pdfRef.current, printStyle);
          let attachments = [
            {
              filebytes: pdf,
              filename: `${formsyConfig.FormDescription}.pdf`,
              filetype: "application/pdf",
            },
          ];

          if (fileAttachments.length > 0) {
            attachments = attachments.concat(
              fileAttachments
                .filter(
                  (attachment) => attachment.FileType === "application/pdf"
                )
                .map((attachment) => {
                  return {
                    filebytes: attachment.FileContent.split(",")[1],
                    filename: attachment.FileName,
                    filetype: attachment.FileType,
                  };
                })
            );
          }

          const highPriorityIncidentTypes = [
            "Client/Immediate Family Member Death",
            "Abuse/Neglect Allegation",
          ];
          const incidentType = getFieldValue(data.TypeOfIncident);
          const highPriority =
            highPriorityIncidentTypes.includes(incidentType) ||
            (incidentType === "Suicide Attempt" &&
              getFieldValue(data.SuicideAttemptMedicalAttention) === "Yes");
          const signatures = sigArray.map((sig) => sig.name).join("; ");
          let recipientList = [...config.Signed];

          if (incidentType === "Suicide Attempt") {
            recipientList = recipientList.concat(config.SuicideAttempt);
          }

          sendEmailMessage(
            recipientList,
            undefined,
            `${
              highPriority ? `\u2757 ` : ""
            }Incident Report Final Signed - ${getFieldValue(
              data.TypeOfIncident
            )}`,
            `${getMainEmailBody(selectedProgram)}
${getIncidentDescription()}
${getExtraEmailBody()}
<p><b>Signed by</b>: ${signatures}</p>`,
            undefined,
            attachments,
            highPriority
          );
        }
      }
    } else {
      handleSubmit(
        null,
        formsyDetailsObj,
        formsyDetailsObj.FinalSigned === "1" ? undefined : null,
        sigArray
      );
    }
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <Input
                    name="DateOfIncident"
                    label={data.DateOfIncident["@label"]}
                    error={errors.DateOfIncident}
                    value={getFieldValue(data.DateOfIncident)}
                    onChange={handleChange}
                    identical={compare("DateOfIncident")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <Input
                    name="TimeOfIncident"
                    label={data.TimeOfIncident["@label"]}
                    error={errors.TimeOfIncident}
                    value={getFieldValue(data.TimeOfIncident)}
                    onChange={handleChange}
                    identical={compare("TimeOfIncident")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="time"
                  />
                </div>
              </div>
              <h6>{data.DiscoveryHeader["@label"]}</h6>
              <Input
                name="NotifiedOn"
                label={data.NotifiedOn["@label"]}
                error={errors.NotifiedOn}
                value={getFieldValue(data.NotifiedOn)}
                onChange={handleChange}
                identical={compare("NotifiedOn")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                type="date"
                min={getFieldValue(data.DateOfIncident)}
              />
              <Select
                name="NotifiedBy"
                label={data.NotifiedBy["@label"]}
                error={errors.NotifiedBy}
                value={getFieldValue(data.NotifiedBy)}
                options={JSON.parse(
                  data.NotifiedBy["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                identical={compare("NotifiedBy")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.NotifiedBy) === "Other" ? (
                <Input
                  name="NotifiedByOther"
                  label={data.NotifiedByOther["@label"]}
                  error={errors.NotifiedByOther}
                  value={getFieldValue(data.NotifiedByOther)}
                  onChange={handleChange}
                  identical={compare("NotifiedByOther")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <Select
                name="NotifiedMethod"
                label={data.NotifiedMethod["@label"]}
                error={errors.NotifiedMethod}
                value={getFieldValue(data.NotifiedMethod)}
                options={JSON.parse(
                  data.NotifiedMethod["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                identical={compare("NotifiedMethod")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.NotifiedMethod) === "Other" ? (
                <Input
                  name="NotifiedMethodOther"
                  label={data.NotifiedMethodOther["@label"]}
                  error={errors.NotifiedMethodOther}
                  value={getFieldValue(data.NotifiedMethodOther)}
                  onChange={handleChange}
                  identical={compare("NotifiedMethodOther")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <h6>{data.IncidentHeader["@label"]}</h6>
              <Select
                name="TypeOfIncident"
                label={data.TypeOfIncident["@label"]}
                error={errors.TypeOfIncident}
                value={getFieldValue(data.TypeOfIncident)}
                options={JSON.parse(
                  data.TypeOfIncident["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                identical={compare("TypeOfIncident")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.TypeOfIncident) ===
              "Abuse/Neglect Allegation" ? (
                <>
                  <Select
                    name="AbuseAllegedBy"
                    label={data.AbuseAllegedBy["@label"]}
                    error={errors.AbuseAllegedBy}
                    value={getFieldValue(data.AbuseAllegedBy)}
                    options={JSON.parse(
                      data.AbuseAllegedBy["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("AbuseAllegedBy")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.AbuseAllegedBy) === "Other" ? (
                    <Input
                      name="AbuseAllegedByOther"
                      label={data.AbuseAllegedByOther["@label"]}
                      error={errors.AbuseAllegedByOther}
                      value={getFieldValue(data.AbuseAllegedByOther)}
                      onChange={handleChange}
                      identical={compare("AbuseAllegedByOther")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="AbuseAllegedAgainst"
                    label={data.AbuseAllegedAgainst["@label"]}
                    error={errors.AbuseAllegedAgainst}
                    value={getFieldValue(data.AbuseAllegedAgainst)}
                    onChange={handleChange}
                    identical={compare("AbuseAllegedAgainst")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="AbuseDescription"
                    label={data.AbuseDescription["@label"]}
                    error={errors.AbuseDescription}
                    value={getFieldValue(data.AbuseDescription)}
                    onChange={handleChange}
                    identical={compare("AbuseDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <div className="clearfix">
                    <div className="float-start">
                      {data.AbuseDate["@label"]}
                    </div>
                    <div className="float-start">
                      <FormCheckbox
                        name="AbuseNA"
                        label={data.AbuseNA["@label"]}
                        checked={getFieldValue(data.AbuseNA) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                  </div>
                  {getFieldValue(data.AbuseNA) !== "true" ? (
                    <>
                      <div className="d-flex mt-2">
                        <Input
                          name="AbuseDate"
                          error={errors.AbuseDate}
                          value={getFieldValue(data.AbuseDate)}
                          onChange={handleChange}
                          identical={compare("AbuseDate")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          className="me-2"
                        />
                        <Input
                          name="AbuseTime"
                          error={errors.AbuseTime}
                          value={getFieldValue(data.AbuseTime)}
                          onChange={handleChange}
                          identical={compare("AbuseTime")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="time"
                        />
                      </div>
                      <Input
                        name="AbuseNotes"
                        label={data.AbuseNotes["@label"]}
                        error={errors.AbuseNotes}
                        value={getFieldValue(data.AbuseNotes)}
                        onChange={handleChange}
                        identical={compare("AbuseNotes")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  ) : null}
                  {getFieldValue(data.AbuseNA) === "true" ? (
                    <Input
                      name="AbuseNADetails"
                      label={data.AbuseNADetails["@label"]}
                      error={errors.AbuseNADetails}
                      value={getFieldValue(data.AbuseNADetails)}
                      onChange={handleChange}
                      identical={compare("AbuseNADetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="AbuseStepsTaken"
                    label={data.AbuseStepsTaken["@label"]}
                    error={errors.AbuseStepsTaken}
                    value={getFieldValue(data.AbuseStepsTaken)}
                    onChange={handleChange}
                    identical={compare("AbuseStepsTaken")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Select
                    name="AbuseOutcome"
                    label={data.AbuseOutcome["@label"]}
                    error={errors.AbuseOutcome}
                    value={getFieldValue(data.AbuseOutcome)}
                    options={JSON.parse(
                      data.AbuseOutcome["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("AbuseOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) ===
              "Client/Immediate Family Member Death" ? (
                <>
                  <RadioButton
                    name="DeathClient"
                    label={data.DeathClient["@label"]}
                    error={errors.DeathClient}
                    value={getFieldValue(data.DeathClient)}
                    options={JSON.parse(
                      data.DeathClient["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.DeathClient) === "No" ? (
                    <Input
                      name="DeathWho"
                      label={data.DeathWho["@label"]}
                      error={errors.DeathWho}
                      value={getFieldValue(data.DeathWho)}
                      onChange={handleChange}
                      identical={compare("DeathWho")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <RadioButton
                    name="DeathInvestigation"
                    label={data.DeathInvestigation["@label"]}
                    error={errors.DeathInvestigation}
                    value={getFieldValue(data.DeathInvestigation)}
                    options={JSON.parse(
                      data.DeathInvestigation["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Select
                    name="DeathWhere"
                    label={data.DeathWhere["@label"]}
                    error={errors.DeathWhere}
                    value={getFieldValue(data.DeathWhere)}
                    options={JSON.parse(
                      data.DeathWhere["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("DeathWhere")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.DeathWhere) === "Other" ? (
                    <Input
                      name="DeathWhereOther"
                      label={data.DeathWhereOther["@label"]}
                      error={errors.DeathWhereOther}
                      value={getFieldValue(data.DeathWhereOther)}
                      onChange={handleChange}
                      identical={compare("DeathWhereOther")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="DeathDescription"
                    label={data.DeathDescription["@label"]}
                    error={errors.DeathDescription}
                    value={getFieldValue(data.DeathDescription)}
                    onChange={handleChange}
                    identical={compare("DeathDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="DeathPoliceReport"
                    label={data.DeathPoliceReport["@label"]}
                    error={errors.DeathPoliceReport}
                    value={getFieldValue(data.DeathPoliceReport)}
                    options={JSON.parse(
                      data.DeathPoliceReport["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.DeathPoliceReport) === "Yes" ? (
                    <>
                      <Input
                        name="DeathReportedBy"
                        label={data.DeathReportedBy["@label"]}
                        error={errors.DeathReportedBy}
                        value={getFieldValue(data.DeathReportedBy)}
                        onChange={handleChange}
                        identical={compare("DeathReportedBy")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="DeathReportInfo"
                        label={data.DeathReportInfo["@label"]}
                        error={errors.DeathReportInfo}
                        value={getFieldValue(data.DeathReportInfo)}
                        onChange={handleChange}
                        identical={compare("DeathReportInfo")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  ) : null}
                  {getFieldValue(data.DeathPoliceReport) === "No" ? (
                    <Input
                      name="DeathNoReportReason"
                      label={data.DeathNoReportReason["@label"]}
                      error={errors.DeathNoReportReason}
                      value={getFieldValue(data.DeathNoReportReason)}
                      onChange={handleChange}
                      identical={compare("DeathNoReportReason")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="DeathStepsTaken"
                    label={data.DeathStepsTaken["@label"]}
                    error={errors.DeathStepsTaken}
                    value={getFieldValue(data.DeathStepsTaken)}
                    onChange={handleChange}
                    identical={compare("DeathStepsTaken")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.DeathLastDate["@label"]}
                  <div className="d-flex mt-2">
                    <Input
                      name="DeathLastDate"
                      error={errors.DeathLastDate}
                      value={getFieldValue(data.DeathLastDate)}
                      onChange={handleChange}
                      identical={compare("DeathLastDate")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                      className="me-2"
                    />
                    <Input
                      name="DeathLastTime"
                      label={data.DeathLastTime["@label"]}
                      error={errors.DeathLastTime}
                      value={getFieldValue(data.DeathLastTime)}
                      onChange={handleChange}
                      identical={compare("DeathLastTime")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="time"
                    />
                  </div>
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) ===
              "Homicidal Thoughts, Gestures, Threats" ? (
                <>
                  <Input
                    name="HomicidalThoughtsDescription"
                    label={data.HomicidalThoughtsDescription["@label"]}
                    error={errors.HomicidalThoughtsDescription}
                    value={getFieldValue(data.HomicidalThoughtsDescription)}
                    onChange={handleChange}
                    identical={compare("HomicidalThoughtsDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="HomicidalThoughtsInjury"
                    label={data.HomicidalThoughtsInjury["@label"]}
                    error={errors.HomicidalThoughtsInjury}
                    value={getFieldValue(data.HomicidalThoughtsInjury)}
                    options={JSON.parse(
                      data.HomicidalThoughtsInjury["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.HomicidalThoughtsInjury) === "Yes" ? (
                    <Input
                      name="HomicidalThoughtsInjuryDetails"
                      label={data.HomicidalThoughtsInjuryDetails["@label"]}
                      error={errors.HomicidalThoughtsInjuryDetails}
                      value={getFieldValue(data.HomicidalThoughtsInjuryDetails)}
                      onChange={handleChange}
                      identical={compare("HomicidalThoughtsInjuryDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Select
                    name="HomicidalThoughtsOutcome"
                    label={data.HomicidalThoughtsOutcome["@label"]}
                    error={errors.HomicidalThoughtsOutcome}
                    value={getFieldValue(data.HomicidalThoughtsOutcome)}
                    options={JSON.parse(
                      data.HomicidalThoughtsOutcome["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    identical={compare("HomicidalThoughtsOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.HomicidalThoughtsOutcome)?.includes(
                    "**"
                  ) ? (
                    <>
                      <RadioButton
                        name="HomicidalThoughtsSafetyPlan"
                        label={data.HomicidalThoughtsSafetyPlan["@label"]}
                        error={errors.HomicidalThoughtsSafetyPlan}
                        value={getFieldValue(data.HomicidalThoughtsSafetyPlan)}
                        options={JSON.parse(
                          data.HomicidalThoughtsSafetyPlan[
                            "@options"
                          ].replaceAll("'", '"')
                        )}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {getFieldValue(data.HomicidalThoughtsSafetyPlan) ===
                      "No" ? (
                        <Input
                          name="HomicidalThoughtsNoSafetyPlanReason"
                          label={
                            data.HomicidalThoughtsNoSafetyPlanReason["@label"]
                          }
                          error={errors.HomicidalThoughtsNoSafetyPlanReason}
                          value={getFieldValue(
                            data.HomicidalThoughtsNoSafetyPlanReason
                          )}
                          onChange={handleChange}
                          identical={compare(
                            "HomicidalThoughtsNoSafetyPlanReason"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </>
                  ) : null}
                  <div className="clearfix">
                    <div className="float-start">
                      {data.HomicidalThoughtsDate["@label"]}
                    </div>
                    <div className="float-start">
                      <Checkbox
                        name="HomicidalThoughtsNA"
                        label={data.HomicidalThoughtsNA["@label"]}
                        checked={
                          getFieldValue(data.HomicidalThoughtsNA) === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                  </div>
                  {getFieldValue(data.HomicidalThoughtsNA) !== "true" ? (
                    <div className="d-flex">
                      <Input
                        name="HomicidalThoughtsDate"
                        error={errors.HomicidalThoughtsDate}
                        value={getFieldValue(data.HomicidalThoughtsDate)}
                        onChange={handleChange}
                        identical={compare("HomicidalThoughtsDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="date"
                        className="me-2"
                      />
                      <Input
                        name="HomicidalThoughtsTime"
                        label={data.HomicidalThoughtsTime["@label"]}
                        error={errors.HomicidalThoughtsTime}
                        value={getFieldValue(data.HomicidalThoughtsTime)}
                        onChange={handleChange}
                        identical={compare("HomicidalThoughtsTime")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="time"
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) ===
              "Psychiatric Emergency, excluding SI, HI or Self-Harm" ? (
                <>
                  <Input
                    name="PsychiatricEmergencyDescription"
                    label={data.PsychiatricEmergencyDescription["@label"]}
                    error={errors.PsychiatricEmergencyDescription}
                    value={getFieldValue(data.PsychiatricEmergencyDescription)}
                    onChange={handleChange}
                    identical={compare("PsychiatricEmergencyDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Select
                    name="PsychiatricEmergencyOutcome"
                    label={data.PsychiatricEmergencyOutcome["@label"]}
                    error={errors.PsychiatricEmergencyOutcome}
                    value={getFieldValue(data.PsychiatricEmergencyOutcome)}
                    options={JSON.parse(
                      data.PsychiatricEmergencyOutcome["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    identical={compare("PsychiatricEmergencyOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.PsychiatricEmergencyOutcome)?.includes(
                    "**"
                  ) ? (
                    <>
                      <RadioButton
                        name="PsychiatricEmergencySafetyPlan"
                        label={data.PsychiatricEmergencySafetyPlan["@label"]}
                        error={errors.PsychiatricEmergencySafetyPlan}
                        value={getFieldValue(
                          data.PsychiatricEmergencySafetyPlan
                        )}
                        options={JSON.parse(
                          data.PsychiatricEmergencySafetyPlan[
                            "@options"
                          ].replaceAll("'", '"')
                        )}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {getFieldValue(data.PsychiatricEmergencySafetyPlan) ===
                      "No" ? (
                        <Input
                          name="PsychiatricEmergencyNoSafetyPlanReason"
                          label={
                            data.PsychiatricEmergencyNoSafetyPlanReason[
                              "@label"
                            ]
                          }
                          error={errors.PsychiatricEmergencyNoSafetyPlanReason}
                          value={getFieldValue(
                            data.PsychiatricEmergencyNoSafetyPlanReason
                          )}
                          onChange={handleChange}
                          identical={compare(
                            "PsychiatricEmergencyNoSafetyPlanReason"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </>
                  ) : null}
                  <div className="clearfix">
                    <div className="float-start">
                      {data.PsychiatricEmergencyDate["@label"]}
                    </div>
                    <div className="float-start">
                      <FormCheckbox
                        name="PsychiatricEmergencyNA"
                        label={data.PsychiatricEmergencyNA["@label"]}
                        checked={
                          getFieldValue(data.PsychiatricEmergencyNA) === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                  </div>
                  {getFieldValue(data.PsychiatricEmergencyNA) !== "true" ? (
                    <div className="d-flex mt-2">
                      <Input
                        name="PsychiatricEmergencyDate"
                        error={errors.PsychiatricEmergencyDate}
                        value={getFieldValue(data.PsychiatricEmergencyDate)}
                        onChange={handleChange}
                        identical={compare("PsychiatricEmergencyDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="date"
                        className="me-2"
                      />
                      <Input
                        name="PsychiatricEmergencyTime"
                        error={errors.PsychiatricEmergencyTime}
                        value={getFieldValue(data.PsychiatricEmergencyTime)}
                        onChange={handleChange}
                        identical={compare("PsychiatricEmergencyTime")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="time"
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) === "Self-Harm Behaviors" ? (
                <>
                  <RadioButton
                    name="SelfHarmBehaviorsSuicidal"
                    label={data.SelfHarmBehaviorsSuicidal["@label"]}
                    error={errors.SelfHarmBehaviorsSuicidal}
                    value={getFieldValue(data.SelfHarmBehaviorsSuicidal)}
                    options={JSON.parse(
                      data.SelfHarmBehaviorsSuicidal["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={({ currentTarget: input }) => {
                      const dataObj = handleChange({ currentTarget: input });

                      if (input.value === "Yes") {
                        dataObj.TypeOfIncident["@value"] =
                          "Suicidal Thoughts, Gestures, Threats";
                        setData(dataObj);
                      }
                    }}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="SelfHarmBehaviorsDescription"
                    label={data.SelfHarmBehaviorsDescription["@label"]}
                    error={errors.SelfHarmBehaviorsDescription}
                    value={getFieldValue(data.SelfHarmBehaviorsDescription)}
                    onChange={handleChange}
                    identical={compare("SelfHarmBehaviorsDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="SelfHarmBehaviorsInjury"
                    label={data.SelfHarmBehaviorsInjury["@label"]}
                    error={errors.SelfHarmBehaviorsInjury}
                    value={getFieldValue(data.SelfHarmBehaviorsInjury)}
                    options={JSON.parse(
                      data.SelfHarmBehaviorsInjury["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.SelfHarmBehaviorsInjury) === "Yes" ? (
                    <Input
                      name="SelfHarmBehaviorsInjuryDetails"
                      label={data.SelfHarmBehaviorsInjuryDetails["@label"]}
                      error={errors.SelfHarmBehaviorsInjuryDetails}
                      value={getFieldValue(data.SelfHarmBehaviorsInjuryDetails)}
                      onChange={handleChange}
                      identical={compare("SelfHarmBehaviorsInjuryDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <RadioButton
                    name="SelfHarmBehaviorsChronic"
                    label={data.SelfHarmBehaviorsChronic["@label"]}
                    error={errors.SelfHarmBehaviorsChronic}
                    value={getFieldValue(data.SelfHarmBehaviorsChronic)}
                    options={JSON.parse(
                      data.SelfHarmBehaviorsChronic["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.SelfHarmBehaviorsChronic) === "Yes" ? (
                    <Input
                      name="SelfHarmBehaviorsChronicTreatment"
                      label={data.SelfHarmBehaviorsChronicTreatment["@label"]}
                      error={errors.SelfHarmBehaviorsChronicTreatment}
                      value={getFieldValue(
                        data.SelfHarmBehaviorsChronicTreatment
                      )}
                      onChange={handleChange}
                      identical={compare("SelfHarmBehaviorsChronicTreatment")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <RadioButton
                    name="SelfHarmBehaviorsSafetyPlan"
                    label={data.SelfHarmBehaviorsSafetyPlan["@label"]}
                    error={errors.SelfHarmBehaviorsSafetyPlan}
                    value={getFieldValue(data.SelfHarmBehaviorsSafetyPlan)}
                    options={JSON.parse(
                      data.SelfHarmBehaviorsSafetyPlan["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.SelfHarmBehaviorsSafetyPlan) === "No" ? (
                    <Input
                      name="SelfHarmBehaviorsNoSafetyPlanReason"
                      label={data.SelfHarmBehaviorsNoSafetyPlanReason["@label"]}
                      error={errors.SelfHarmBehaviorsNoSafetyPlanReason}
                      value={getFieldValue(
                        data.SelfHarmBehaviorsNoSafetyPlanReason
                      )}
                      onChange={handleChange}
                      identical={compare("SelfHarmBehaviorsNoSafetyPlanReason")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) ===
              "Suicidal Thoughts, Gestures, Threats" ? (
                <>
                  <Input
                    name="SuicidalThoughtsDescription"
                    label={data.SuicidalThoughtsDescription["@label"]}
                    error={errors.SuicidalThoughtsDescription}
                    value={getFieldValue(data.SuicidalThoughtsDescription)}
                    onChange={handleChange}
                    identical={compare("SuicidalThoughtsDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="SuicidalThoughtsCSSRs"
                    label={data.SuicidalThoughtsCSSRs["@label"]}
                    error={errors.SuicidalThoughtsCSSRs}
                    value={getFieldValue(data.SuicidalThoughtsCSSRs)}
                    options={JSON.parse(
                      data.SuicidalThoughtsCSSRs["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <div className="mb-4">
                    {getFieldValue(data.SuicidalThoughtsCSSRs) === "Yes"
                      ? data.SuicidalThoughtsCSSRsLabel["@label"]
                      : null}
                    {getFieldValue(data.SuicidalThoughtsCSSRs) === "No" ? (
                      <Input
                        name="SuicidalThoughtsNoCSSRsReason"
                        label={data.SuicidalThoughtsNoCSSRsReason["@label"]}
                        error={errors.SuicidalThoughtsNoCSSRsReason}
                        value={getFieldValue(
                          data.SuicidalThoughtsNoCSSRsReason
                        )}
                        onChange={handleChange}
                        identical={compare("SuicidalThoughtsNoCSSRsReason")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                  <Select
                    name="SuicidalThoughtsOutcome"
                    label={data.SuicidalThoughtsOutcome["@label"]}
                    error={errors.SuicidalThoughtsOutcome}
                    value={getFieldValue(data.SuicidalThoughtsOutcome)}
                    options={JSON.parse(
                      data.SuicidalThoughtsOutcome["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    identical={compare("SuicidalThoughtsOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.SuicidalThoughtsOutcome)?.includes(
                    "**"
                  ) ? (
                    <>
                      <RadioButton
                        name="SuicidalThoughtsSafetyPlan"
                        label={data.SuicidalThoughtsSafetyPlan["@label"]}
                        error={errors.SuicidalThoughtsSafetyPlan}
                        value={getFieldValue(data.SuicidalThoughtsSafetyPlan)}
                        options={JSON.parse(
                          data.SuicidalThoughtsSafetyPlan[
                            "@options"
                          ].replaceAll("'", '"')
                        )}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {getFieldValue(data.SuicidalThoughtsSafetyPlan) ===
                      "No" ? (
                        <Input
                          name="SuicidalThoughtsNoSafetyPlanReason"
                          label={
                            data.SuicidalThoughtsNoSafetyPlanReason["@label"]
                          }
                          error={errors.SuicidalThoughtsNoSafetyPlanReason}
                          value={getFieldValue(
                            data.SuicidalThoughtsNoSafetyPlanReason
                          )}
                          onChange={handleChange}
                          identical={compare(
                            "SuicidalThoughtsNoSafetyPlanReason"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </>
                  ) : null}
                  <div className="clearfix">
                    <div className="float-start">
                      {data.SuicidalThoughtsDate["@label"]}
                    </div>
                    <div className="float-start">
                      <FormCheckbox
                        name="SuicidalThoughtsNA"
                        label={data.SuicidalThoughtsNA["@label"]}
                        checked={
                          getFieldValue(data.SuicidalThoughtsNA) === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                  </div>
                  {getFieldValue(data.SuicidalThoughtsNA) !== "true" ? (
                    <div className="d-flex mt-2">
                      <Input
                        name="SuicidalThoughtsDate"
                        error={errors.SuicidalThoughtsDate}
                        value={getFieldValue(data.SuicidalThoughtsDate)}
                        onChange={handleChange}
                        identical={compare("SuicidalThoughtsDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="date"
                        className="me-2"
                      />
                      <Input
                        name="SuicidalThoughtsTime"
                        error={errors.SuicidalThoughtsTime}
                        value={getFieldValue(data.SuicidalThoughtsTime)}
                        onChange={handleChange}
                        identical={compare("SuicidalThoughtsTime")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="time"
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) === "Suicide Attempt" ? (
                <>
                  <Input
                    name="SuicideAttemptDescription"
                    label={data.SuicideAttemptDescription["@label"]}
                    error={errors.SuicideAttemptDescription}
                    value={getFieldValue(data.SuicideAttemptDescription)}
                    onChange={handleChange}
                    identical={compare("SuicideAttemptDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="SuicideAttemptMedicalAttention"
                    label={data.SuicideAttemptMedicalAttention["@label"]}
                    error={errors.SuicideAttemptMedicalAttention}
                    value={getFieldValue(data.SuicideAttemptMedicalAttention)}
                    options={JSON.parse(
                      data.SuicideAttemptMedicalAttention[
                        "@options"
                      ].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.SuicideAttemptMedicalAttention) ===
                  "Yes" ? (
                    <Input
                      name="SuicideAttemptMedicalAttentionDetails"
                      label={
                        data.SuicideAttemptMedicalAttentionDetails["@label"]
                      }
                      error={errors.SuicideAttemptMedicalAttentionDetails}
                      value={getFieldValue(
                        data.SuicideAttemptMedicalAttentionDetails
                      )}
                      onChange={handleChange}
                      identical={compare(
                        "SuicideAttemptMedicalAttentionDetails"
                      )}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <RadioButton
                    name="SuicideAttemptExistingSafetyPlan"
                    label={data.SuicideAttemptExistingSafetyPlan["@label"]}
                    error={errors.SuicideAttemptExistingSafetyPlan}
                    value={getFieldValue(data.SuicideAttemptExistingSafetyPlan)}
                    options={JSON.parse(
                      data.SuicideAttemptExistingSafetyPlan[
                        "@options"
                      ].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.SuicideAttemptExistingSafetyPlan) ===
                  "No" ? (
                    <Input
                      name="SuicideAttemptNoExistingSafetyPlanReason"
                      label={
                        data.SuicideAttemptNoExistingSafetyPlanReason["@label"]
                      }
                      error={errors.SuicideAttemptNoExistingSafetyPlanReason}
                      value={getFieldValue(
                        data.SuicideAttemptNoExistingSafetyPlanReason
                      )}
                      onChange={handleChange}
                      identical={compare(
                        "SuicideAttemptNoExistingSafetyPlanReason"
                      )}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Select
                    name="SuicideAttemptOutcome"
                    label={data.SuicideAttemptOutcome["@label"]}
                    error={errors.SuicideAttemptOutcome}
                    value={getFieldValue(data.SuicideAttemptOutcome)}
                    options={JSON.parse(
                      data.SuicideAttemptOutcome["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    identical={compare("SuicideAttemptOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.SuicideAttemptOutcome)?.includes("**") ? (
                    <>
                      <RadioButton
                        name="SuicideAttemptSafetyPlan"
                        label={data.SuicideAttemptSafetyPlan["@label"]}
                        error={errors.SuicideAttemptSafetyPlan}
                        value={getFieldValue(data.SuicideAttemptSafetyPlan)}
                        options={JSON.parse(
                          data.SuicideAttemptSafetyPlan["@options"].replaceAll(
                            "'",
                            '"'
                          )
                        )}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {getFieldValue(data.SuicideAttemptSafetyPlan) === "No" ? (
                        <Input
                          name="SuicideAttemptNoSafetyPlanReason"
                          label={
                            data.SuicideAttemptNoSafetyPlanReason["@label"]
                          }
                          error={errors.SuicideAttemptNoSafetyPlanReason}
                          value={getFieldValue(
                            data.SuicideAttemptNoSafetyPlanReason
                          )}
                          onChange={handleChange}
                          identical={compare(
                            "SuicideAttemptNoSafetyPlanReason"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </>
                  ) : null}
                  <div className="clearfix">
                    <div className="float-start">
                      {data.SuicideAttemptDate["@label"]}
                    </div>
                    <div className="float-start">
                      <FormCheckbox
                        name="SuicideAttemptNA"
                        label={data.SuicideAttemptNA["@label"]}
                        checked={
                          getFieldValue(data.SuicideAttemptNA) === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                  </div>
                  {getFieldValue(data.SuicideAttemptNA) !== "true" ? (
                    <div className="d-flex my-2">
                      <Input
                        name="SuicideAttemptDate"
                        error={errors.SuicideAttemptDate}
                        value={getFieldValue(data.SuicideAttemptDate)}
                        onChange={handleChange}
                        identical={compare("SuicideAttemptDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="date"
                        className="me-2"
                      />
                      <Input
                        name="SuicideAttemptTime"
                        label={data.SuicideAttemptTime["@label"]}
                        error={errors.SuicideAttemptTime}
                        value={getFieldValue(data.SuicideAttemptTime)}
                        onChange={handleChange}
                        identical={compare("SuicideAttemptTime")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="time"
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) ===
              "Illegal Behavior (to include substance use)" ? (
                <>
                  <Select
                    name="IllegalBehaviorWhere"
                    label={data.IllegalBehaviorWhere["@label"]}
                    error={errors.IllegalBehaviorWhere}
                    value={getFieldValue(data.IllegalBehaviorWhere)}
                    options={JSON.parse(
                      data.IllegalBehaviorWhere["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("FieldName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.IllegalBehaviorWhere) === "Other" ? (
                    <Input
                      name="IllegalBehaviorWhereOther"
                      label={data.IllegalBehaviorWhereOther["@label"]}
                      error={errors.IllegalBehaviorWhereOther}
                      value={getFieldValue(data.IllegalBehaviorWhereOther)}
                      onChange={handleChange}
                      identical={compare("IllegalBehaviorWhereOther")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="IllegalBehaviorDescription"
                    label={data.IllegalBehaviorDescription["@label"]}
                    error={errors.IllegalBehaviorDescription}
                    value={getFieldValue(data.IllegalBehaviorDescription)}
                    onChange={handleChange}
                    identical={compare("IllegalBehaviorDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="IllegalBehaviorHistory"
                    label={data.IllegalBehaviorHistory["@label"]}
                    error={errors.IllegalBehaviorHistory}
                    value={getFieldValue(data.IllegalBehaviorHistory)}
                    options={JSON.parse(
                      data.IllegalBehaviorHistory["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.IllegalBehaviorHistory) === "Yes" ? (
                    <Input
                      name="IllegalBehaviorHistoryDetails"
                      label={data.IllegalBehaviorHistoryDetails["@label"]}
                      error={errors.IllegalBehaviorHistoryDetails}
                      value={getFieldValue(data.IllegalBehaviorHistoryDetails)}
                      onChange={handleChange}
                      identical={compare("IllegalBehaviorHistoryDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="IllegalBehaviorFollowUp"
                    label={data.IllegalBehaviorFollowUp["@label"]}
                    error={errors.IllegalBehaviorFollowUp}
                    value={getFieldValue(data.IllegalBehaviorFollowUp)}
                    onChange={handleChange}
                    identical={compare("IllegalBehaviorFollowUp")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="IllegalBehaviorArrested"
                    label={data.IllegalBehaviorArrested["@label"]}
                    error={errors.IllegalBehaviorArrested}
                    value={getFieldValue(data.IllegalBehaviorArrested)}
                    options={JSON.parse(
                      data.IllegalBehaviorArrested["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Select
                    name="IllegalBehaviorOutcome"
                    label={data.IllegalBehaviorOutcome["@label"]}
                    error={errors.IllegalBehaviorOutcome}
                    value={getFieldValue(data.IllegalBehaviorOutcome)}
                    options={JSON.parse(
                      data.IllegalBehaviorOutcome["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    identical={compare("IllegalBehaviorOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.IllegalBehaviorOutcome) === "Other" ? (
                    <Input
                      name="IllegalBehaviorOutcomeOther"
                      label={data.IllegalBehaviorOutcomeOther["@label"]}
                      error={errors.IllegalBehaviorOutcomeOther}
                      value={getFieldValue(data.IllegalBehaviorOutcomeOther)}
                      onChange={handleChange}
                      identical={compare("IllegalBehaviorOutcomeOther")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) ===
              "Aggression/Destruction towards SAFY Employee/SAFY Property" ? (
                <>
                  <Select
                    name="AggressionWhere"
                    label={data.AggressionWhere["@label"]}
                    error={errors.AggressionWhere}
                    value={getFieldValue(data.AggressionWhere)}
                    options={JSON.parse(
                      data.AggressionWhere["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("AggressionWhere")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.AggressionWhere) === "Other" ? (
                    <Input
                      name="AggressionWhereOther"
                      label={data.AggressionWhereOther["@label"]}
                      error={errors.AggressionWhereOther}
                      value={getFieldValue(data.AggressionWhereOther)}
                      onChange={handleChange}
                      identical={compare("AggressionWhereOther")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="AggressionDescription"
                    label={data.AggressionDescription["@label"]}
                    error={errors.AggressionDescription}
                    value={getFieldValue(data.AggressionDescription)}
                    onChange={handleChange}
                    identical={compare("AggressionDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="AggressionKnown"
                    label={data.AggressionKnown["@label"]}
                    error={errors.AggressionKnown}
                    value={getFieldValue(data.AggressionKnown)}
                    options={JSON.parse(
                      data.AggressionKnown["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.AggressionKnown) === "Yes" ? (
                    <Input
                      name="AggressionKnownDetails"
                      label={data.AggressionKnownDetails["@label"]}
                      error={errors.AggressionKnownDetails}
                      value={getFieldValue(data.AggressionKnownDetails)}
                      onChange={handleChange}
                      identical={compare("AggressionKnownDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <Input
                    name="AggressionFollowUp"
                    label={data.AggressionFollowUp["@label"]}
                    error={errors.AggressionFollowUp}
                    value={getFieldValue(data.AggressionFollowUp)}
                    onChange={handleChange}
                    identical={compare("AggressionFollowUp")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Select
                    name="AggressionOutcome"
                    label={data.AggressionOutcome["@label"]}
                    error={errors.AggressionOutcome}
                    value={getFieldValue(data.AggressionOutcome)}
                    options={JSON.parse(
                      data.AggressionOutcome["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("AggressionOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.AggressionOutcome) === "Other" ? (
                    <Input
                      name="AggressionOutcomeOther"
                      label={data.AggressionOutcomeOther["@label"]}
                      error={errors.AggressionOutcomeOther}
                      value={getFieldValue(data.AggressionOutcomeOther)}
                      onChange={handleChange}
                      identical={compare("AggressionOutcomeOther")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) ===
              "Car Accident with SAFY Employee" ? (
                <>
                  <Input
                    name="CarAccidentDescription"
                    label={data.CarAccidentDescription["@label"]}
                    error={errors.CarAccidentDescription}
                    value={getFieldValue(data.CarAccidentDescription)}
                    onChange={handleChange}
                    identical={compare("CarAccidentDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="CarAccidentMedicalAttention"
                    label={data.CarAccidentMedicalAttention["@label"]}
                    error={errors.CarAccidentMedicalAttention}
                    value={getFieldValue(data.CarAccidentMedicalAttention)}
                    options={JSON.parse(
                      data.CarAccidentMedicalAttention["@options"].replaceAll(
                        "'",
                        '"'
                      )
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.CarAccidentMedicalAttention) === "Yes" ? (
                    <Input
                      name="CarAccidentMedicationAttentionDetails"
                      label={
                        data.CarAccidentMedicationAttentionDetails["@label"]
                      }
                      error={errors.CarAccidentMedicationAttentionDetails}
                      value={getFieldValue(
                        data.CarAccidentMedicationAttentionDetails
                      )}
                      onChange={handleChange}
                      identical={compare(
                        "CarAccidentMedicationAttentionDetails"
                      )}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </>
              ) : null}
              {getFieldValue(data.TypeOfIncident) === "Other" ? (
                <>
                  <Input
                    name="OtherTypeDescription"
                    label={data.OtherTypeDescription["@label"]}
                    error={errors.OtherTypeDescription}
                    value={getFieldValue(data.OtherTypeDescription)}
                    onChange={handleChange}
                    identical={compare("OtherTypeDescription")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="OtherTypeStepsTaken"
                    label={data.OtherTypeStepsTaken["@label"]}
                    error={errors.OtherTypeStepsTaken}
                    value={getFieldValue(data.OtherTypeStepsTaken)}
                    onChange={handleChange}
                    identical={compare("OtherTypeStepsTaken")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Select
                    name="OtherTypeOutcome"
                    label={data.OtherTypeOutcome["@label"]}
                    error={errors.OtherTypeOutcome}
                    value={getFieldValue(data.OtherTypeOutcome)}
                    options={JSON.parse(
                      data.OtherTypeOutcome["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("OtherTypeOutcome")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.OtherTypeOutcome) === "Other" ? (
                    <Input
                      name="OtherTypeOutcomeOther"
                      label={data.OtherTypeOutcomeOther["@label"]}
                      error={errors.OtherTypeOutcomeOther}
                      value={getFieldValue(data.OtherTypeOutcomeOther)}
                      onChange={handleChange}
                      identical={compare("OtherTypeOutcomeOther")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </>
              ) : null}
              <h6>{data.NotificationsHeader["@label"]}</h6>
              <RadioButton
                name="OtherNotificationsNeeded"
                label={data.OtherNotificationsNeeded["@label"]}
                error={errors.OtherNotificationsNeeded}
                value={getFieldValue(data.OtherNotificationsNeeded)}
                options={JSON.parse(
                  data.OtherNotificationsNeeded["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.OtherNotificationsNeeded["@value"] === "Yes" ? (
                <>
                  <Input
                    name="OtherNotifiedName"
                    label={data.OtherNotifiedName["@label"]}
                    error={errors.OtherNotifiedName}
                    value={getFieldValue(data.OtherNotifiedName)}
                    onChange={handleChange}
                    identical={compare("OtherNotifiedName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="OtherNotifiedWhen"
                    label={data.OtherNotifiedWhen["@label"]}
                    error={errors.OtherNotifiedWhen}
                    value={getFieldValue(data.OtherNotifiedWhen)}
                    onChange={handleChange}
                    identical={compare("OtherNotifiedWhen")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="OtherNotifiedComments"
                    label={data.OtherNotifiedComments["@label"]}
                    error={errors.OtherNotifiedComments}
                    value={getFieldValue(data.OtherNotifiedComments)}
                    onChange={handleChange}
                    identical={compare("OtherNotifiedComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              {formsyDetails.FinalSigned === "1" ? (
                <>
                  <div className="border rounded my-4 p-2 bg-light">
                    <div className="h6">{data.ResolutionHeader["@label"]}</div>
                    <div className="my-4">
                      {resolutionAmendmentsMemo.map((item) => (
                        <div className="mt-2 mb-4 mx-4" key={item._id}>
                          <div className="w-100">
                            <div
                              className={`mx-4 h6 ${
                                safyInfo.user.displayName === item.CreatedBy
                                  ? " text-end"
                                  : "text-start"
                              }`}
                            >
                              {`${item.CreatedBy} - ${new Date(
                                item.CreatedOn
                              ).toLocaleString()}`}
                            </div>
                          </div>
                          <div className="border rounded bg-white p-2">{`${item.Amendment}`}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mb-4">
                      <Input
                        name="NewAmendment"
                        label="New Resolution/Amendment:"
                        onChange={({ currentTarget: input }) => {
                          setNewAmendment(input.value);
                        }}
                        value={newAmendment}
                      />
                    </div>
                    <div className="row">
                      <div className="col">
                        <div
                          className="btn btn-success float-end me-4 mb-2"
                          onClick={() => addAmendment()}
                        >
                          Add
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
              marginBottom="100px"
            />
            {submissionid ? (
              <FileUpload
                header={<div className="h5">Upload documentation here:</div>}
                objectId={submissionid}
                objectType="formsy"
                savedFiles={fileAttachments}
                printImg={true}
                loadFiles={loadFiles}
              />
            ) : null}
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SAFYILIncidentReport;
